/* SettingsSectionRenderer.js */

"use strict";
const _ = require("lodash");
const UiUtils = require("../seededcomponents/uiutils/index");
const ModalUserFeedback = require("@gluenetworks/steve_ui_widgets").UserFeedback.ModalUserFeedback;

class SettingsSectionRenderer {
    constructor(model, logger, layoutManager) {
        this._logger = logger;
        this._model = model;

        this._rootElem = document.createElement("section-content-gl");
        this._rootElem.component = this._model;

        this._layoutManager = layoutManager;
        this._rootElem.appendChild(this._layoutManager.renderer.rootElement);

        //TODO -replace UiUtils with MUF
        this._uiUtils = new UiUtils();
        this._layoutManager._di.registry.register("$teve.ui.UiUtils", this._uiUtils);

        this._modalUserFeedback = new ModalUserFeedback();
        this._layoutManager._di.registry.register("$teve.ui.userFeedback", this._modalUserFeedback);
        this._modalUserFeedback.renderer.rootElement.style.top = "8rem";

        this._moreElement = document.createElement("more-item");
        this._moreElement.innerHTML = "<span>More</span>";
        this._moreElement.setAttribute("style", "visibility: hidden");
        this._registerMoreButtonClick();

        this._moreItemsContainer = document.createElement("more-links-container");
        this._moreItemsContainer.innerHTML = "<span class='arrow-up'></span>";
        this._moreItems = document.createElement("more-links");

        this._moreItemsContainer.appendChild(this._moreItems);
        this._moreElement.appendChild(this._moreItemsContainer);

        this._delayedCollect = _.debounce(this._collect.bind(this), 500);

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        this._unregisterListeners();
        this._unregisterMoreButtonClick();
        return Promise.all([
            this._layoutManager.shutdown(),
            this._modalUserFeedback.shutdown(),
        ]).then(
            () => {
                this._rootElem.remove();
                this._layoutManager = undefined;
                this._rootElem = undefined;
                this._model = undefined;
                this._moreElement = undefined;
                this._moreItemsContainer = undefined;
                this._moreItems = undefined;
                this._delayedCollect = undefined;
            },
            (err) => {
                this._logger.error(err, "Error shutting down Layout Manager.");
            }
        );
    }

    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("RouteUpdated", (this._routeUpdated = this._routeUpdated.bind(this)));
        this._model.on(
            "OrganizationChanged",
            (this._organizationChanged = this._organizationChanged.bind(this))
        );
        this._layoutManager.on("UpdateRoute", (this._updateRoute = this._updateRoute.bind(this)));

        window.addEventListener(
            "resize",
            (this._onWindowResize = _.debounce(this._onWindowResize.bind(this), 200))
        );
        window.addEventListener(
            "mousedown",
            (this._onWindowClick = this._onWindowClick.bind(this))
        );
    }

    _unregisterListeners() {
        this._model
            .removeAllListeners("Shutdown")
            .removeAllListeners("RouteUpdated")
            .removeAllListeners("OrganizationChanged");
        this._layoutManager.removeAllListeners("UpdateRoute");
        window.removeEventListener("resize", this._onWindowResize);
        window.removeEventListener("mousedown", this._onWindowClick);
    }

    _registerMoreButtonClick() {
        this._moreElement.addEventListener(
            "click",
            (this._onMoreButtonClick = this._onMoreButtonClick.bind(this))
        );
    }
    _unregisterMoreButtonClick() {
        this._moreElement.removeEventListener("click", this._onMoreButtonClick);
    }

    _collect() {
        const navItems = this._layoutManager._renderer._pageNavCmpnt._renderer._navItems;
        const navContainer = this._layoutManager._renderer._pageNavCmpnt._renderer.getRootElement();
        const childNavItems = navItems.length;
        const screenWidth = window.innerWidth;

        const emptyNavsMore = (
            emptyCount = [...this._moreItems.children].length,
            resetMoreEl = true
        ) => {
            [...this._moreItems.children]
                .slice(0, emptyCount)
                .forEach((itemToMoveBack) =>
                    navContainer.insertBefore(itemToMoveBack, this._moreElement)
                );
            if (resetMoreEl) {
                this._moreItems.innerHTML = "";
                this._moreElement.setAttribute("style", "visibility: hidden;");
            }
        };

        //reset nav to init state for accurate width/count calcs
        emptyNavsMore();

        let elemWidth = navItems.reduce((acc, item) => acc + item.offsetWidth, 0);
        elemWidth = elemWidth + (screenWidth <= elemWidth ? 0 : 83);

        //fit  needs to be calculated one less than necessary to avoid hits with reserved scrollbar space
        let fitCount = Math.floor((screenWidth / elemWidth) * childNavItems) - 2;

        if (screenWidth >= elemWidth - 20) {
            fitCount = childNavItems;
        }

        if (fitCount < childNavItems) {
            this._moreElement.removeAttribute("style");
            navContainer.appendChild(this._moreElement);

            emptyNavsMore(fitCount - 1, false);

            const collectedSet = navItems.slice(fitCount);

            this._moreElement.removeAttribute("style");
            collectedSet.forEach((itemToMove) => this._moreItems.appendChild(itemToMove));
            this._toggleMenu(false);
        } else if (fitCount >= childNavItems) {
            emptyNavsMore(fitCount - 1);
        }
    }

    _toggleMenu(state) {
        if (state) {
            this._moreItemsContainer.removeAttribute("style");
        } else {
            this._moreItemsContainer.setAttribute("style", "visibility: hidden;");
        }
    }

    _routeUpdated(layoutOptions) {
        this._modalUserFeedback.hideDisplayed();
        if (this._uiUtils._confirmDialog) {
            this._uiUtils._confirmDialog.renderer._cancelButton._triggerUserClick();
        }
        if (this._uiUtils._messageDialog) {
            this._uiUtils._messageDialog.renderer._confirmButton._triggerUserClick();
        }
        this._layoutManager.setLayoutSelections(layoutOptions);
        this._onWindowResize();
    }

    _updateRoute(routeInfo) {
        this._modalUserFeedback.hideDisplayed();
        this.model.updateRoute(routeInfo);
        this._onWindowResize();
    }

    _organizationChanged(activeOrg) {
        this._layoutManager.organizationChanged(activeOrg);
        this._delayedCollect();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let selectionOptions = {};
        let routeInfo = this.model.getRouteInformation();
        if (routeInfo.subRoute) {
            let parsedNewRoute = routeInfo.subRoute.split("/");
            selectionOptions.groupName = parsedNewRoute[0];
            selectionOptions.pageName = parsedNewRoute[1];
        }
        if (routeInfo.pageData) {
            selectionOptions.pageData = routeInfo.pageData;
        }
        return this._layoutManager.setLayoutSelections(selectionOptions).then(() => {
            this._layoutManager.renderer.rootElement.insertBefore(
                this._modalUserFeedback.renderer.rootElement,
                this._layoutManager.renderer._pageContainer
            );
            this._uiUtils.rootElement = this._layoutManager.renderer.rootElement;
            // this._onWindowResize();
            this._layoutManager.renderer.rootElement.className = selectionOptions.pageName;
            this._model._setHelpLink(selectionOptions);
            this._collect();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this._shutdown();
    }

    _onWindowResize() {
        this._collect();
    }

    _onWindowClick(event) {
        if (
            event.target.nodeName !== "NAV-ITEM" &&
            event.target.parentElement.nodeName !== "MORE-ITEM"
        ) {
            this._toggleMenu(false);
        }
    }

    _onMoreButtonClick(event) {
        event.stopPropagation();
        this._toggleMenu(this._moreItemsContainer.hasAttribute("style") ? true : false);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = SettingsSectionRenderer;
